/**
 * IM Manager
 * 管理 IMSDK 的初始化、连接、事件监听等
 */

import MlImSdk from '@ml/im-sdk'
import { SlsReporter } from '@ml/im-sdk/apm'
import { IM_CONTENT_TYPE } from '@/common/constants'
import { IM_EVENT_TYPE } from '@/common/constants/imEventTypes'
import { groupBy, isString } from 'lodash'
import { emitMessage } from '@/events/messageEvents'

const SLS_CONFIG = {
  project: import.meta.env.VITE_SLS_PROJECT,
  logstore: import.meta.env.VITE_SLS_LOGSTORE,
  endpoint: import.meta.env.VITE_SLS_ENDPOINT,
  source: `webapp-${import.meta.env.MODE}`
}

class IMManager {
  constructor() {
    this.im = null
    this.slsReporter = null
    this.isInitialized = false
    this.isConnecting = false
  }

  /**
   * 初始化 IMSDK ✅
   * @param {Object} config - IM配置
   * @param {string} config.zy_token - IM Token
   * @param {string} config.userId - 用户ID
   * @param {string} config.httpUrl - HTTP基础URL
   * @param {string} config.zy_im_config - IM配置（JSON字符串）
   * @returns {Promise<void>}
   */
  async initialize(config) {
    if (this.isInitialized) {
      console.warn('[IMManager] Already initialized')
      return
    }

    const { zy_token, userId, httpUrl, zy_im_config } = config

    if (!zy_token || !userId || !zy_im_config) {
      throw new Error('[IMManager] Missing required config: zy_token, userId, or zy_im_config')
    }

    try {
      console.log('[IMManager] Initializing IMSDK...', { userId, httpUrl, zy_im_config, zy_token })

      // 初始化 IMSDK（zy_im_config 直接传递，不做任何解析）
      this.im = new MlImSdk({
        zy_token,
        userId,
        httpUrl,
        zy_im_config
      })

      // 注册事件监听器
      this.#registerEventListeners()

      // 初始化 APM SLS 上报
      if (SLS_CONFIG.project && SLS_CONFIG.logstore && SLS_CONFIG.endpoint) {
        this.slsReporter = new SlsReporter(SLS_CONFIG)
        this.slsReporter.bindSdk(this.im)
        console.log('[IMManager] SLS reporter initialized')
      }

      this.isInitialized = true
      console.log('[IMManager] IMSDK initialized successfully')
    } catch (error) {
      console.error('[IMManager] Failed to initialize IMSDK:', error)
      throw error
    }
  }

  /**
   * 建立 IM 连接 ✅
   * @returns {Promise<void>}
   */
  async connect() {
    // 检查 IMSDK 是否初始化
    if (!this.isInitialized || !this.im) {
      throw new Error('[IMManager] IMSDK not initialized')
    }

    // 检查是否正在连接
    if (this.isConnecting) {
      console.warn('[IMManager] Already connecting')
      return
    }

    try {
      console.log('[IMManager] Connecting to IM server...')
      await this.im.connect()
      this.isConnecting = true
    } catch (error) {
      console.error('[IMManager] Failed to connect:', error)
      this.isConnecting = false
      throw error
    }
  }

  /**
   * 断开 IM 连接 ✅
   */
  disconnect() {
    if (this.im) {
      console.log('[IMManager] Disconnecting from IM server...')
      this.im.disconnect()
      this.isConnecting = false
    }
  }

  /**
   * 重新连接 ✅
   * @param {Object} config - 新的配置（可选）
   */
  async reconnect(config) {
    if (this.isInitialized && this.im && config) {
      this.isConnecting = false
      await this.im.reConnect(config)
      this.isConnecting = true
    }
  }

  /**
   * 注册事件监听器
   * 将 SDK 事件通过 EventBus 派发出去
   * @private
   */
  #registerEventListeners() {
    if (!this.im) return

    // 连接成功 - 通过 EventBus 派发
    this.im.on('connected', (data) => {
      console.log('[IMManager] Connected to IM server:', data)
      this.isConnecting = false
      emitMessage(IM_EVENT_TYPE.CONNECTED, data)
    })

    /**
     * 连接断开 - 通过 EventBus 派发
     * 🔔：token 过期、服务端主动断开、IM断了之后IM主动重连不上就会收到这个通知
     * 🔔：如果是主动调用 disconnect 方法，则不会收到这个通知
     * ⚠️：测试环境如果 token 过期了，服务端没有主动断开连接，所以不会收到这个通知（马翔宇）
     */
    this.im.on('disconnected', (data) => {
      console.log('[IMManager] Disconnected from IM server', data)
      this.isConnecting = false
      emitMessage(IM_EVENT_TYPE.DISCONNECTED, data)
    })

    // 收到消息（立即处理，不使用队列和节流）
    this.im.on('message', (messages = []) => {
      if (messages.length === 0) return

      console.log(`[IMManager] Received ${messages.length} messages from SDK:`, messages)

      // 立即处理消息，让具体的 Handler 决定是否需要节流
      this.#processMessages(messages)
    })

    // 被踢下线 - 通过 EventBus 派发
    this.im.on('kickOut', (data) => {
      console.warn('[IMManager] Kicked out from another device:', data)
      emitMessage(IM_EVENT_TYPE.KICK_OUT, data)
    })

    // 认证失败 - 通过 EventBus 派发
    this.im.on('authFailed', (error) => {
      console.error('[IMManager] Authentication failed:', error)
      emitMessage(IM_EVENT_TYPE.AUTH_FAILED, error)
    })

    // 发生错误 - 通过 EventBus 派发
    this.im.on('error', (error) => {
      console.error('[IMManager] IM error:', error)
      emitMessage(IM_EVENT_TYPE.ERROR, error)
    })

    /**
     * Token 刷新 - 通过 EventBus 派发
     * 🔔：
     * 当 IM 服务器通知 token 即将失效时，会收到这个通知，
     * 失效前5分钟会开始通知，如果没有续签成功，会在 IM 下一个心跳再次通知
     * 直到 token 过期，收到 disconnected 通知
     * 🔔：续签成功之后 1 分钟内，还是有几率会收到这个通知
     * ⚠️：测试环境如果 token 过期了，服务端没有主动断开连接，所以还会收到这个通知（马翔宇）
     */
    this.im.on('refreshToken', () => {
      console.log('[IMManager] Token refresh requested by IM server')
      emitMessage(IM_EVENT_TYPE.REFRESH_TOKEN)
    })
  }

  /**
   * 批量解析自定义消息（CUSTOMER 类型）
   * 职责：只负责解析 JSON 和按 msgType 分组，不处理业务逻辑
   *
   * @param {Object[]} messages - SDK 原始消息数组
   * @private
   */
  #batchParseCustomerMessages(messages = []) {
    // 1. 解析所有消息的 content 字段（JSON 反序列化）
    const parsedMessages = messages.map(({ data }) => {
      try {
        let { content = "" } = data
        content = isString(content) ? JSON.parse(content) : content
        return content
      } catch (error) {
        console.error('[IMManager] Failed to parse content:', error, data)
        return null
      }
    }).filter(Boolean)

    // 2. 按 msgType 分组
    const groupedByMsgType = groupBy(parsedMessages, content => content.msgType)

    // 3. 遍历所有 msgType，透传到 EventBus（不做业务判断）
    for (const msgType in groupedByMsgType) {
      const messagesOfType = groupedByMsgType[msgType]

      // ✅ 直接派发，由 Plugin/Handler 层决定如何处理
      // ✅ IMManager 不关心具体的消息类型和业务逻辑
      emitMessage(msgType, messagesOfType)
    }
  }

  /**
   * 处理消息（立即处理，不使用队列）
   * ## IM 中的推送类型
   *
   * enum ContentType {
   *   UNKNOWN_CONTENT_TYPE = 0;
   *   // 可见消息文本类型
   *   TEXT      = 1;
   *   PICTURE   = 2;
   *   VOICE     = 3;
   *   VIDEO     = 4;
   *   FILE      = 5;
   *   QUOTE     = 6;
   *   RICH_TEXT = 7;
   *   CUSTOMER = 100; // 用户自定义类型，业务一般用这个类型，对应的 content 用 TextMsg
   * }
   * @private
   */
  #processMessages(messages) {
    if (messages.length === 0) return

    // 1. 使用 lodash.groupBy 按 contentType 分组
    const groupedByContentType = groupBy(messages, msg => {
      const contentType = msg.header?.contentType
      return contentType !== undefined ? contentType : IM_CONTENT_TYPE.UNKNOW_CONTENT_TYPE
    })

    // 2. 按 contentType 分别处理（立即处理）
    for (const contentType in groupedByContentType) {
      switch (Number(contentType)) {
        case IM_CONTENT_TYPE.CUSTOMER:
          this.#batchParseCustomerMessages(groupedByContentType[contentType])
          break
        default:
          console.log(`[IMManager] Ignoring ${groupedByContentType[contentType].length} messages of contentType: ${contentType} (not implemented yet)`)
          break
      }
    }
  }

  /**
   * 更新 IM Token
   * 当收到 Token 刷新请求时，业务侧获取新 token 后调用此方法
   * @param {string} newToken - 新的 IM Token
   */
  updateToken(newToken) {
    if (!this.im) {
      console.error('[IMManager] IM SDK not initialized')
      return
    }

    if (!newToken) {
      console.error('[IMManager] New token is required')
      return
    }

    console.log('[IMManager] Updating IM token...')
    this.im.refreshToken(newToken)
  }

  /**
   * 清理资源
   */
  destroy() {
    console.log('[IMManager] Destroying...')

    this.disconnect()
    this.slsReporter?.destroy()
    this.slsReporter = null
    this.im = null
    this.isInitialized = false
    this.isConnecting = false
  }
}

// 导出单例
export const imManager = new IMManager()

export default IMManager
